import React from "react";
import { Switch, Tag, Tooltip } from "@bikdotai/bik-component-library";
import { WhatsNewArticle } from "../types/whatsNew.types";
import EditIcon from "../../../icons/editIcon";
import ArchiveIcon from "../../../icons/ArchiveIcon";
import { WhatsNewTableService } from "../services/whatsNewTable.service";

export const isProd = process.env.REACT_APP_IS_PROD === "true";

interface WhatsNewTableRowProps {
  article: WhatsNewArticle;
  onEdit: (article: WhatsNewArticle) => void;
  onArchive: (article: WhatsNewArticle) => void;
  onVisibilityToggle: (article: WhatsNewArticle, visibility: boolean) => void;
  onPlayVideo: (videoUrl: string) => void;
}

const getBaseUrl = (article: WhatsNewArticle) => {
  const modules = Array.isArray(article.module)
    ? article.module
    : article.module
      ? [article.module]
      : [];

  const hasManifest = modules.some((module) =>
    module.toLowerCase().includes("manifest")
  );

  if (hasManifest) {
    return isProd
      ? "https://dashboard.getmanifest.ai"
      : "https://bik-dot-ai-git-open-ai-staging-bikdotai.vercel.app";
  }

  return isProd
    ? "https://dashboard.bik.ai"
    : "https://staging.dashboard.bik.ai";
};

const getFeatureTestUrl = (article: WhatsNewArticle) => {
  const baseUrl = getBaseUrl(article);
  const pageUrl = article.pageUrls?.[0] || "";

  // Ensure proper URL formatting
  let fullUrl = baseUrl;
  if (pageUrl.startsWith("/")) {
    fullUrl += pageUrl;
  } else if (pageUrl) {
    fullUrl += `/${pageUrl}`;
  }

  // Add featureId as query parameter
  if (article.docId) {
    fullUrl += `${fullUrl.includes("?") ? "&" : "?"}featureId=${article.docId}`;
  }

  return fullUrl;
};

export const createWhatsNewTableRow = ({
  article,
  onEdit,
  onArchive,
  onVisibilityToggle,
  onPlayVideo,
}: WhatsNewTableRowProps) => {
  const cellContainerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    minHeight: "80px",
  };

  const renderFeatureTitle = () => (
    <div style={cellContainerStyle}>
      <div
        style={{
          maxWidth: "250px",
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          lineHeight: "1.4",
          fontSize: "14px",
          fontWeight: "500",
          wordWrap: "break-word",
          whiteSpace: "normal",
          textAlign: "left",
        }}
      >
        {article.title}
      </div>
    </div>
  );

  const renderFeatureDescription = () => (
    <div style={cellContainerStyle}>
      <div
        style={{
          maxWidth: "400px",
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          lineHeight: "1.4",
          fontSize: "14px",
          color: "#666",
          wordWrap: "break-word",
          whiteSpace: "normal",
          textAlign: "left",
        }}
        dangerouslySetInnerHTML={{
          __html: article.body || "No description",
        }}
      />
    </div>
  );

  const renderVisibility = () => (
    <div style={cellContainerStyle}>
      <Switch
        value={article.visibility !== undefined ? article.visibility : false}
        onValueChange={async (newValue: boolean) => {
          onVisibilityToggle(article, newValue);
        }}
        type="brand"
      />
    </div>
  );

  const renderUpdates = () => (
    <div style={cellContainerStyle}>{article.featureUpdateType || "Minor"}</div>
  );

  const renderShowInFeed = () => {
    if (article.featureUpdateType === "Major") {
      return (
        <div style={cellContainerStyle}>
          <Tag
            tagText="N/A"
            theme="light"
            type="white"
            onLeadingIconClicked={() => {}}
            onTrailingIconClicked={() => {}}
          />
        </div>
      );
    }
    const inFeed = article.showInFeed !== false;
    return (
      <div style={cellContainerStyle}>
        <Tag
          tagText={inFeed ? "In Feed" : "Hidden"}
          theme="light"
          type={inFeed ? "positive" : "warning"}
          onLeadingIconClicked={() => {}}
          onTrailingIconClicked={() => {}}
        />
      </div>
    );
  };

  const renderModule = () => {
    const modules = Array.isArray(article.module)
      ? article.module
      : article.module
        ? [article.module]
        : ["General"];

    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "4px",
          alignItems: "flex-start",
          alignContent: "flex-start",
          justifyContent: "flex-start",
          minHeight: "80px",
        }}
      >
        {modules.map((module, index) => (
          <Tag
            key={index}
            tagText={module}
            theme="light"
            type="white"
            onLeadingIconClicked={() => {}}
            onTrailingIconClicked={() => {}}
          />
        ))}
      </div>
    );
  };

  const renderDate = () => (
    <div style={cellContainerStyle}>
      {article.createdAt ? (
        <span style={{ fontSize: "12px", fontWeight: "500" }}>
          {new Date(article.createdAt.toDate()).toLocaleDateString()}
        </span>
      ) : (
        "No date"
      )}
    </div>
  );

  const renderVisibleUntil = () => (
    <div style={cellContainerStyle}>
      {article.expirationDate
        ? new Date(article.expirationDate.toDate()).toLocaleDateString()
        : "No date"}
    </div>
  );

  const renderActions = () => {
    const testUrl = getFeatureTestUrl(article);

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: "80px",
          width: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "16px",
            alignItems: "center",
          }}
        >
          <span
            onClick={() => {
              if (article.productVideo) {
                onPlayVideo(article.productVideo);
              } else {
                alert("No video available");
              }
            }}
            style={WhatsNewTableService.getLinkStyle()}
          >
            {article.productVideo ? "Play Video" : "No Video"}
          </span>
          <span
            onClick={() => {
              window.open(testUrl, "_blank");
            }}
            style={WhatsNewTableService.getLinkStyle()}
          >
            Test
          </span>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "8px",
            overflow: "visible",
          }}
        >
          <Tooltip tooltipContent={<span>Edit</span>}>
            <div onClick={() => onEdit(article)} style={{ cursor: "pointer" }}>
              <EditIcon height={24} width={24} />
            </div>
          </Tooltip>
          <Tooltip
            tooltipContent={
              <span>{article.archived ? "Unarchive" : "Archive"}</span>
            }
          >
            <div
              onClick={() => onArchive(article)}
              style={{ cursor: "pointer" }}
            >
              {article.archived ? (
                <ArchiveIcon height={24} width={24} color="green" />
              ) : (
                <ArchiveIcon height={24} width={24} />
              )}
            </div>
          </Tooltip>
        </div>
      </div>
    );
  };

  return {
    featureTitle: renderFeatureTitle(),
    featureDescription: renderFeatureDescription(),
    visibility: renderVisibility(),
    updates: renderUpdates(),
    showInFeed: renderShowInFeed(),
    module: renderModule(),
    date: renderDate(),
    visibleUntil: renderVisibleUntil(),
    actions: renderActions(),
  };
};
